import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import Collapse from '@mui/material/Collapse';

import CardForm from './CardForm';
import MuiInput from './MuiInput';

import CredixLogo from 'images/gateway/credix-checkout-grande.png';
import DaviviendaLogo from 'images/gateway/davivienda-checkout.png';

import *  as shapeProps from 'utils/shape-props';

import { ACCORDION_OPTIONS } from '../CuotasGatewayForm';

import { useGatewayRegionAccount } from '@hooks/GatewayRegionAccountContext';

const propTypes = {
  ...shapeProps.formikProps,
  resetForm: PropTypes.func.isRequired,

  // options
  credomaticInstallments: PropTypes.array.isRequired,
  credixInstallments: PropTypes.array.isRequired,

  // Accordion state
  activeAccordion: PropTypes.string,
  setActiveAccordion: PropTypes.func.isRequired,

  // Credix pin
  credixPinEnabled: PropTypes.bool,
  setCredixPinEnabled: PropTypes.func.isRequired,
}

const defaultProps = {

}

// Lightweight accordion card replacing react-bootstrap Accordion/Card/Collapse.
// The expand/collapse uses MUI's Collapse transition; the header/body styling
// stays identical via the self-contained `.credomatic-accordion` rules in
// gateway.scss.
const AccordionCard = ({ eventKey, activeKey, onToggle, header, children }) => (
  <div className="card">
    <div
      className={`card-header flex justify-between ${eventKey == activeKey ? "open" : ""}`}
      role="button"
      onClick={() => onToggle(eventKey)}
    >
      {header}
    </div>
    <Collapse in={eventKey == activeKey}>
      <div className="card-body">
        {children}
      </div>
    </Collapse>
  </div>
);

const CredomaticForm = (props) => {
  const regionData = useGatewayRegionAccount();
  const { activeAccordion, setActiveAccordion, credixPinEnabled, setCredixPinEnabled } = props;

  // Preserves react-bootstrap Accordion toggle semantics (clicking the open
  // panel collapses it → null).
  const handleToggle = (eventKey) => setActiveAccordion(activeAccordion == eventKey ? null : eventKey);

  const credixPinToggle = () => {
    const newValue = !credixPinEnabled;
    props.setFieldValue("credix_pin", ""); // Reset pin value each time the user toggles the pin form
    setCredixPinEnabled(newValue);
  }

  // When changing the active accordion, reset the form and set the first installment as default
  useEffect(() => {
    props.resetForm();
    if (activeAccordion == ACCORDION_OPTIONS.credomatic) {
      // console.log("WHY", props.credomaticInstallment)
      props.setFieldValue("credomatic_payment_id", props.credomaticInstallments[0].value);
    }
    else if (activeAccordion == ACCORDION_OPTIONS.credix) {
      props.setFieldValue("credix_payment_id", props.credixInstallments[0].value);
    } else {
      props.setFieldValue("davivienda_payment_id", props.daviviendaInstallments[0].value);
    }

  }, [activeAccordion]);

  // TODO Optimize all accordion active conditions

  return (
    <>
      <h2 className="fs18 mb12">
        <strong>
          { regionData.cuotasFormTitle }
        </strong>
      </h2>
      <div className='credomatic-accordion'>
        {/* Credomatic */}
        <AccordionCard
          eventKey={ACCORDION_OPTIONS.credomatic}
          activeKey={activeAccordion}
          onToggle={handleToggle}
          header={
            <>
              <LegacyRadioHeader title={ regionData.cuotasCredomaticTitle } activeKey={activeAccordion} eventKey={ACCORDION_OPTIONS.credomatic} />
              <span className='cards-area'>
                { regionData.cuotasCredomaticLogoComponent }
              </span>
            </>
          }
        >
          {/* Credomatic installments select */}
          <div className='mui-input filled'>
            <label>Cuotas</label>
            <select name="credomatic_payment_id" onChange={props.handleChange} value={props.values.credomatic_payment_id}>
              {props.credomaticInstallments.map(item => (
                <option value={item.value} key={item.value}>{item.label}</option>
              ))}
            </select>
          </div>

          <CardForm
            label="Nombre del titular"
            values={props.values}
            handleChange={props.setFieldValue}
          />
        </AccordionCard>

        {/* CREDIX */}
        { !regionData.disableCredix &&
          <AccordionCard
            eventKey={ACCORDION_OPTIONS.credix}
            activeKey={activeAccordion}
            onToggle={handleToggle}
            header={
              <>
                <LegacyRadioHeader title="Credix" activeKey={activeAccordion} eventKey={ACCORDION_OPTIONS.credix} />
                <span className='cards-area'>
                  <img className="credix-logo" src={CredixLogo} alt='Credix' />
                </span>
              </>
            }
          >
            {/* Credix installments select */}
            <div className='mui-input filled'>
              <label>{`Cuotas ${props.values.credix_payment_id}`}</label> {/* Cuotas 3, Cuotas 12, etc. */}
              <select name="credix_payment_id" onChange={props.handleChange} value={props.values.credix_payment_id}>
                {props.credixInstallments.map(item => (
                  <option value={item.value} key={item.value}>{item.label}</option>
                ))}
              </select>
            </div>

            {credixPinEnabled ? (
              <MuiInput
                name="credix_pin"
                value={props.values.credix_pin}
                label="PIN"
                type="tel"
                onChange={props.handleChange}
              />
            ) : (
              <CardForm
                label="Nombre del titular"
                values={props.values}
                handleChange={props.setFieldValue}
              />
            )}

            <button type="button" className="fs14 black-link p0" onClick={credixPinToggle}>
              {credixPinEnabled ? "Pagar con Tarjeta" : "Pagar con Pin Credix"}
            </button>
          </AccordionCard>
        }

        {/* Davivienda */}
        { !regionData.disableDavivienda && (
          <AccordionCard
            eventKey={ACCORDION_OPTIONS.davivienda}
            activeKey={activeAccordion}
            onToggle={handleToggle}
            header={
              <>
                <LegacyRadioHeader title="Paguitos Davivienda" activeKey={activeAccordion} eventKey={ACCORDION_OPTIONS.davivienda} />
                <span className='cards-area'>
                  <img className="davivienda-logo" src={DaviviendaLogo} alt='Davivienda' />
                </span>
              </>
            }
          >
            {/* Davivienda installments select */}
            <div className='mui-input filled'>
              <label>{`Cuotas ${props.values.davivienda_payment_id}`}</label> {/* Cuotas 3, Cuotas 18, etc. */}
              <select name="davivienda_payment_id" onChange={props.handleChange} value={props.values.davivienda_payment_id}>
                {props.daviviendaInstallments.map(item => (
                  <option value={item.value} key={item.value}>{item.label}</option>
                ))}
              </select>
              {!!props.values.disable &&
                <div className='mt8'>
                  <span className='fs14'>
                    Plazos de <strong>18 y 24 meses</strong> exclusivos para tarjetas <strong>Davivienda Unimart</strong>. <a className='black' target='_blank' href="https://www.unimart.com/pages/aplicacion-tarjeta-davivienda-unimart"><u>Aplica aquí</u></a>
                  </span>
                </div>
              }
            </div>

            <CardForm
              label="Nombre del titular"
              values={props.values}
              handleChange={props.setFieldValue}
            />
          </AccordionCard>
        )}
      </div>
    </>
  )
};

const LegacyRadioHeader = ({ activeKey, eventKey, title }) => (
  <div className='flex align-items-center'>
    <label className={`accordion-radio-placeholder${eventKey == activeKey ? " active" : ""}`}><span></span></label>
    <h5 className='mb0 ml4 fs14'>{title}</h5>
  </div>
)

CredomaticForm.propTypes = propTypes;
CredomaticForm.defaultProps = defaultProps;

export default CredomaticForm;
